import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3944";

export interface TicketMessage {
  id: string;
  sender_type: 'user' | 'staff';
  sender_name: string;
  message: string;
  attachments?: any[];
  created_at: string;
}

export interface TicketData {
  id: string;
  ticket_id: string;
  merchant_code: string;
  subject: string;
  description: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  category: string;
  assigned_to?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  closed_at?: string;
  messages: TicketMessage[];
  message_count: number;
  last_user_message_at?: string | null;
  last_staff_message_at?: string | null;
  merchant_viewed_at?: string | null;
  unread_by_merchant?: boolean;
}

export interface CreateTicketRequest {
  subject: string;
  description: string;
  email: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  category: string;
}

export interface UpdateTicketRequest {
  status?: 'open' | 'in_progress' | 'resolved' | 'closed';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  assigned_to?: string;
}

export interface SendMessageRequest {
  message: string;
  attachments?: any[];
}

export interface TicketStats {
  total_tickets: number;
  open_tickets: number;
  in_progress_tickets: number;
  resolved_tickets: number;
  urgent_tickets: number;
  average_response_time: number;
}

export const ticketService = {
  // Get all tickets for merchant
  async getTickets(): Promise<TicketData[]> {
    const token = localStorage.getItem("token");
    const response = await axios.get(`${API_URL}/tickets`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  // Get ticket by ID
  async getTicket(ticketId: string): Promise<TicketData> {
    const token = localStorage.getItem("token");
    const response = await axios.get(`${API_URL}/tickets/${ticketId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  // Create new ticket
  async createTicket(ticketData: CreateTicketRequest): Promise<TicketData> {
    const token = localStorage.getItem("token");
    const response = await axios.post(`${API_URL}/tickets`, ticketData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  // Update ticket
  async updateTicket(ticketId: string, updateData: UpdateTicketRequest): Promise<TicketData> {
    const token = localStorage.getItem("token");
    const response = await axios.put(`${API_URL}/tickets/${ticketId}`, updateData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  // Send message to ticket
  async sendMessage(ticketId: string, messageData: SendMessageRequest): Promise<TicketMessage> {
    const token = localStorage.getItem("token");
    const response = await axios.post(`${API_URL}/tickets/${ticketId}/messages`, messageData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  // Get ticket statistics
  async getTicketStats(): Promise<TicketStats> {
    const token = localStorage.getItem("token");
    const response = await axios.get(`${API_URL}/tickets/stats`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  // Get ticket categories
  async getCategories(): Promise<string[]> {
    const token = localStorage.getItem("token");
    const response = await axios.get(`${API_URL}/tickets/categories`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  }
};